import { useState, type ReactNode } from "react";
import { toast } from "sonner";

import { ConfirmationDialog } from "~/components/ui/confirmation-dialog";
import type { KeyManager } from "~/lib/crypto";
import { useDeleteGoalOfLife } from "~/lib/queries/goalOfLifeQuery";

import { GoalFormDrawer, type GoalToEdit } from "./goal-form-drawer";
import type { GoalItem } from "./goal-groups";
import { GoalPanel, type DecryptedGoal } from "./goal-panel";
import { ShareGoalDialog } from "./share-goal-dialog";

type FormState = {
  goalToEdit: GoalToEdit | null;
  initialCategoryId?: string;
};

/** A goal whose public link the client creates or manages, with the text the link publishes. */
export type PublicLinkTarget = DecryptedGoal & {
  goalItem: GoalItem;
  categoryName: string;
};

/**
 * The client's goal panel and the forms and dialogs it opens: edit, delete
 * and public link. Every page that lists the client's own goals opens them
 * the same way.
 */
export function useGoalPanels({
  items,
  keyManager,
  isSharedWithCoaches,
}: {
  /** The goals the page lists; the panel reads its goal from them, so a saved change shows at once. */
  items: readonly GoalItem[];
  keyManager: KeyManager | null;
  isSharedWithCoaches: boolean;
}): {
  openGoal: (goalId: string) => void;
  openNewGoal: (categoryId?: string) => void;
  managePublicLink: (target: PublicLinkTarget) => void;
  panels: ReactNode;
} {
  const [openGoalId, setOpenGoalId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState | null>(null);
  const [deleting, setDeleting] = useState<
    (DecryptedGoal & { id: string; stepCount: number }) | null
  >(null);
  const [sharing, setSharing] = useState<PublicLinkTarget | null>(null);
  const deleteGoal = useDeleteGoalOfLife();

  const openItem = items.find((item) => item.goal.id === openGoalId) ?? null;

  const openForm = (state: FormState) => {
    setOpenGoalId(null);
    setForm(state);
  };

  const confirmDelete = () => {
    if (!deleting) return;
    setOpenGoalId(null);
    deleteGoal.mutate(deleting.id, {
      onSuccess: () => {
        toast.success("Goal deleted");
        setDeleting(null);
      },
      onError: () => toast.error("The goal was not deleted. Try again."),
    });
  };

  const panels = (
    <>
      <GoalPanel
        item={openItem}
        open={!!openItem}
        onOpenChange={(open) => !open && setOpenGoalId(null)}
        keyManager={keyManager}
        owner={{
          isSharedWithCoaches,
          onEdit: (decrypted) =>
            openItem &&
            openForm({
              goalToEdit: {
                goal: openItem.goal,
                categoryId: openItem.category.id,
                ...decrypted,
              },
            }),
          onShare: (decrypted) =>
            openItem && setSharing({ ...decrypted, goalItem: openItem }),
          onDelete: (decrypted) =>
            openItem && setDeleting({ ...decrypted, id: openItem.goal.id }),
        }}
      />

      <GoalFormDrawer
        open={!!form}
        onOpenChange={(open) => {
          if (open) return;
          // Closing the edit form returns to the goal it edited.
          if (form?.goalToEdit) setOpenGoalId(form.goalToEdit.goal.id);
          setForm(null);
        }}
        goalToEdit={form?.goalToEdit}
        initialCategoryId={form?.initialCategoryId}
        keyManager={keyManager}
      />

      <ConfirmationDialog
        open={!!deleting}
        onOpenChange={(open) => !open && setDeleting(null)}
        title={deleting ? `Delete "${deleting.title}"?` : "Delete goal?"}
        description={
          deleting
            ? `This deletes the goal${deleting.stepCount > 0 ? ` and its ${deleting.stepCount} ${deleting.stepCount === 1 ? "step" : "steps"}` : ""}. Your coaches and anyone with its public link lose it too. This cannot be undone.`
            : ""
        }
        confirmText="Delete goal"
        variant="destructive"
        onConfirm={confirmDelete}
        isLoading={deleteGoal.isPending}
      />

      {sharing && (
        <ShareGoalDialog
          key={sharing.goalItem.goal.id}
          goalId={sharing.goalItem.goal.id}
          goalTitle={sharing.title}
          goalDescription={sharing.description}
          categoryName={sharing.categoryName}
          shareSlug={sharing.goalItem.goal.shareSlug ?? null}
          keyManager={keyManager}
          open
          onOpenChange={(open) => !open && setSharing(null)}
        />
      )}
    </>
  );

  return {
    openGoal: setOpenGoalId,
    openNewGoal: (categoryId) =>
      openForm({ goalToEdit: null, initialCategoryId: categoryId }),
    managePublicLink: setSharing,
    panels,
  };
}
